import { useEffect, useRef } from 'react';

/**
 * Focusable element selector. Covers all standard interactive elements
 * except those explicitly removed from the tab order.
 */
const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

/**
 * Trap keyboard focus inside a container while `isActive` is true.
 *
 * On activation:
 *   1. Remembers the previously focused element
 *   2. Moves focus to the first focusable child
 *   3. Wraps Tab / Shift+Tab within the container
 *
 * On deactivation:
 *   1. Removes the keydown listener
 *   2. Restores focus to whatever was focused before
 *
 * WCAG 2.1.2 (no keyboard trap outside modals) + 2.4.3 (focus order).
 */
export function useFocusTrap(isActive: boolean) {
  const containerRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!isActive) return;

    // Remember what was focused before the modal opened
    previousFocusRef.current = document.activeElement as HTMLElement;

    // Move focus to the first focusable element in the container.
    // Use a microtask so the DOM has settled after the render that set
    // isActive — without this, the querySelectorAll may run before the
    // modal's children have mounted.
    const timer = setTimeout(() => {
      const first = containerRef.current?.querySelector<HTMLElement>(FOCUSABLE);
      first?.focus();
    }, 0);

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key !== 'Tab' || !containerRef.current) return;

      const focusable = Array.from(
        containerRef.current.querySelectorAll<HTMLElement>(FOCUSABLE),
      );
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('keydown', handleKeyDown);
      // Restore focus when the modal closes
      previousFocusRef.current?.focus();
    };
  }, [isActive]);

  return containerRef;
}
